import React from 'react';
import {
  TEDInputUrvalType,
  IUIUrval,
} from '../../../../../../../Types/UrvalTypes';
import {
  ApolloClient,
  ApolloConsumer,
  MutationFunction as MutationFn,
  FetchResult,
} from '@apollo/client';
import { convertUIUrvalToTEDUrval } from '../../../../../Utils/convertUIUrvalToTEDUrval';
import { convertUrval } from '../../../../SavedUrval/Utils/convertSavedUrval';
import {
  AvenyCollectionMap,
  ModelContextConsumer,
} from '../../../../../../../Model/ModelContext/ModelContext';
import Logger from '../../../../../../../Utils/Logger';
import {
  ModifyUrval as IModifyUrvalMutationData,
  ModifyUrvalVariables as IModifyUrvalMutaionVariables,
} from './_QueryTypes/ModifyUrval';
import mutation from './ModifyUrvalMutation.gql';

const createVariables = (
  uiUrval: IUIUrval,
  urvalInput: TEDInputUrvalType
): IModifyUrvalMutaionVariables | void => {
  const { savedUrvalProps: props } = uiUrval;
  if (props != null) {
    return {
      id: props.id,
      selection: urvalInput,
      conditions: convertUIUrvalToTEDUrval(uiUrval),
      order:
        props.order != null
          ? props.order.map(order => ({
              namn: order.namn,
              ass: order.ass,
              desc: order.desc,
            }))
          : null,
    };
  } else {
    Logger.error(new Error('Tried to modify an unsaved urval'));
  }
};

export type FetchResultType = FetchResult<IModifyUrvalMutationData>;
export type ModifySelectionMutationFnType = MutationFn<
  IModifyUrvalMutationData,
  IModifyUrvalMutaionVariables
>;

/**
 * The component
 */
interface IOuterProps {
  children: (
    modifyUrval: (
      urval: IUIUrval,
      urvalInput: TEDInputUrvalType
    ) => Promise<void>
  ) => React.ReactNode;
  setUrval: (newUrval: IUIUrval) => void;
  collectionName: string;
}

interface IInnerProps extends IOuterProps {
  apolloClient: ApolloClient<any>;
  collectionMap: AvenyCollectionMap;
}

class ModifyUrvalMutation extends React.Component<IInnerProps> {
  modifyUrval = (urval: IUIUrval, urvalInput: TEDInputUrvalType) => {
    const variables = createVariables(urval, urvalInput);

    if (variables != null) {
      return this.props.apolloClient
        .mutate<IModifyUrvalMutationData, IModifyUrvalMutaionVariables>({
          mutation: mutation,
          variables: variables,
        })
        .then(result => {
          if (result.errors != null) {
            throw new Error(result.errors.map(e => e.message).join(', '));
          } else if (
            result.data == null ||
            result.data.modifyselection == null
          ) {
            throw new Error('Got empty response');
          } else {
            // Success, set selection to newly saved selection
            this.props.setUrval(
              convertUrval(
                { ...result.data.modifyselection, conditionPageNames: null },
                this.props.collectionName,
                this.props.collectionMap
              )
            );
          }
        });
    } else {
      throw new Error('Could not put together variables to modify urval');
    }
  };

  render() {
    return this.props.children(this.modifyUrval);
  }
}

const EnhancedModifySelectionMutation = (props: IOuterProps) => (
  <ModelContextConsumer>
    {({ avenyCollectionMap }) => (
      <ApolloConsumer>
        {client => (
          <ModifyUrvalMutation
            {...props}
            apolloClient={client}
            collectionMap={avenyCollectionMap}
          />
        )}
      </ApolloConsumer>
    )}
  </ModelContextConsumer>
);

export default EnhancedModifySelectionMutation;
